import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { CommandeClientsService } from "app/features/commande-client/services/commandes-client.service";
import { SnackService } from "app/shared/services/snack-service.service";
import { TranslateUtils } from "app/shared/utils/translate.utils";
import { Observable, of, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { AuthService } from "../features/auth/services/auth.service";
import { PingService } from "../shared/services/ping.service";

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private skipErrorInterceptor: boolean;
  private error503inProgress: boolean = false;

  constructor(
    private authService: AuthService,
    private snackService: SnackService,
    private commandeClientService: CommandeClientsService,
    private pingService: PingService,
  ) {
    this.skipErrorInterceptor = false;
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    let headers: HttpHeaders = req.headers;
    this.skipErrorInterceptor = headers.has("skip-error-interceptor");
    headers = headers.delete("skip-error-interceptor");

    this.commandeClientService.resetState();

    return this.interceptHttpError(next, req.clone({ headers }));
  }

  /**
   * Vérifie si erreur HTTP interceptée (0 || 404) et affiche un toastr d'erreur.
   * @param {HttpHandler} next
   * @param {HttpRequest<any>} req
   * @returns {Observable<any>}
   */
  private interceptHttpError(
    next: HttpHandler,
    req: HttpRequest<any>,
  ): Observable<any> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        let message = this.resolveErrorMessage(error);

        const chunkFailedMessage = /Loading chunk [\d]+ failed/;

        if (chunkFailedMessage.test(message)) {
          window.location.reload();
        }

        if (req.responseType === "blob") {
          try {
            message = JSON.parse(error?.error?.text()).message;
          } catch (e) {
            message = error?.error?.text()?.toString() ?? error?.error?.text();
          }
        }

        switch (error.status) {
          //case 412:
          //  this.snackService.error(message);
          //  return of(undefined);
          case 0:
            //this.pingService._offline$.next(true);
            return throwError(error);
          case 500:
            this.traiteError(error);
            return throwError(error);
          case 503:
            if (this.error503inProgress) {
              return of(undefined);
            }
            this.error503inProgress = true;
            this.snackService.error(message);
            this.authService.logout();
            setTimeout(() => {
              this.error503inProgress = false;
            }, 1000);
            return of(undefined);
          case 401:
            this.traiteError(error);
            //this.authService.logout();
            return throwError(error);
          case 404:
            this.snackService.error(message);
            return throwError(error);
          //return of(undefined);
          default:
            this.snackService.error(message);
            return throwError(error);
        }
      }),
    );
  }

  /**
   * Résout le message d'erreur : tente de traduire l'errorCode du back,
   * sinon fallback sur le message brut.
   */
  private resolveErrorMessage(error: HttpErrorResponse): string {
    const errorCode = error?.error?.errorCode;
    const params = error?.error?.params;
    const fallback = error?.error?.message;

    if (errorCode) {
      const translated = TranslateUtils.translate(
        `errors.${errorCode}`,
        params,
      );
      // Si la traduction retourne la clé elle-même, c'est qu'elle n'existe pas
      if (translated && translated !== `errors.${errorCode}`) {
        return translated;
      }
    }

    return fallback;
  }

  private traiteError(error: HttpErrorResponse) {
    const apiUrl = new URL(error.url || "");
    const path = apiUrl.pathname;

    // Cherche une règle correspondant à l'URL et dont la condition est vraie
    const matchingRule = this.errorRules.find(
      (rule) =>
        path.startsWith(rule.url) &&
        rule.condition(error.error.message) &&
        (!rule.status || rule.status === error.status),
    );
    if (matchingRule) {
      matchingRule.callback(error?.error.message);
    } else {
      this.snackService.error(this.resolveErrorMessage(error));
    }
  }

  extractTableName(message: string): string | null {
    const match = message.match(
      /violates foreign key constraint .* on table "(.*?)"/,
    );
    return match ? match[1] : null;
  }

  errorRules = [
    {
      url: "/api/specification_etape",
      condition: (message: string) =>
        message.includes("violates foreign key constraint"),
      callback: (message: string) => {
        const tableName = this.extractTableName(message);
        if (tableName) {
          this.snackService.info(
            `Cet élément est lié à un élément "${tableName}". Il sera archivé.`,
          );
        } else {
          this.snackService.info(
            "Cet élément est lié à un autre élément. Il ne peut pas être supprimé",
          );
        }
      },
    },
    {
      url: "/doc/api",
      status: 401,
      condition: (message: string) => {
        return message.includes("Token validation failed");
      },
      callback: (_message: string) => {
        // Avant de bouter l'utilisateur en soft-logout, on tente un refresh
        // SSO : sur les fenêtres détachées (window.open du widget supervision
        // ou de la fiche OF), la première requête doc peut tomber sur un SSO
        // qui vient juste d'expirer dans cette nouvelle session — un simple
        // refresh suffit, pas besoin de redemander le mot de passe.
        this.tryRefreshThenLogout();
      },
    },
  ];

  private async tryRefreshThenLogout(): Promise<void> {
    try {
      await this.authService.ensureRefreshSso();
      console.log("[doc 401] SSO refreshed — user kept logged in");
    } catch (e) {
      console.log("[doc 401] SSO refresh failed → soft logout", e);
      this.authService.softLogout();
    }
  }
}
